import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE } from './config';
import socket, { connectSocket } from '../socket/socket';
import PlayerSearchDropdown from './PlayerSearchDropdown';
import useMobileViewportZoom from './useMobileViewportZoom';
import AdminSidebar from './AdminSidebar';
import '../css/MatchPlanner.css';

const getToken = () => localStorage.getItem('token');
const statusLabel = status => status === 'READY' ? 'พร้อมลง' : status === 'RESERVED' ? 'จองรอบถัดไป' : status;
const pairKey = (a, b) => [Number(a), Number(b)].sort((x, y) => x - y).join('-');

function localAlternatives(item, protectedPairs = []) {
  const players = [...(item.team1 || []), ...(item.team2 || [])];
  if (players.length !== 4) return item.alternatives?.length ? item.alternatives : [item];
  const fallbackPatterns = [
    [[players[0], players[1]], [players[2], players[3]]],
    [[players[0], players[2]], [players[1], players[3]]],
    [[players[0], players[3]], [players[1], players[2]]]
  ];
  const protectedKeys = new Set(protectedPairs.map(pair => pairKey(pair.user_a, pair.user_b)));
  const respectsProtectedPairs = (team1, team2) => [...protectedKeys].every(key => {
    const [a, b] = key.split('-').map(Number);
    const selected = players.filter(player => [a, b].includes(Number(player.user_id))).length;
    if (selected === 0) return true;
    if (selected === 1) return false;
    return [team1, team2].some(team => team.some(p => Number(p.user_id) === a) && team.some(p => Number(p.user_id) === b));
  });
  const calculatedFallbacks = fallbackPatterns
    .filter(([team1, team2]) => respectsProtectedPairs(team1, team2))
    .map(([team1, team2]) => {
    const sum1 = team1.reduce((sum, player) => sum + Number(player.point || 0), 0);
    const sum2 = team2.reduce((sum, player) => sum + Number(player.point || 0), 0);
    return { ...item, team1, team2, sum1, sum2, teamGap: Math.abs(sum1 - sum2) };
  }).sort((a, b) => a.teamGap - b.teamGap);
  const serverAlternatives = (item.alternatives || [])
    .map(alternative => ({ ...item, ...alternative }))
    .filter(alternative => respectsProtectedPairs(alternative.team1, alternative.team2));
  // Include locally calculated arrangements as well. The server may return
  // only the best partition, which previously made this button alternate
  // merely between Team 1 and Team 2 (1/2) instead of rotating all 4 players.
  const base = [...serverAlternatives, ...calculatedFallbacks];
  if (!base.length) base.push(item);
  const everySideOrder = base.flatMap(alternative => [
    alternative,
    { ...alternative, team1: alternative.team2, team2: alternative.team1, sum1: alternative.sum2, sum2: alternative.sum1 }
  ]);
  const seen = new Set();
  return everySideOrder.filter(alternative => {
    const signature = alternative.team1.map(player => player.user_id).join('-') + '|' + alternative.team2.map(player => player.user_id).join('-');
    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });
}

function MatchPlanner() {
  useMobileViewportZoom();

  const { groupId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState('');
  const [variantIndexes, setVariantIndexes] = useState({});
  const [notice, setNotice] = useState('');
  const [editingMatchId, setEditingMatchId] = useState('');
  const [editedMatches, setEditedMatches] = useState({});
  const [approvedMatches, setApprovedMatches] = useState({});
  const [controlOpen, setControlOpen] = useState(false);
  const [waitingPlayersOpen, setWaitingPlayersOpen] = useState(false);
  const [controlData, setControlData] = useState({ queue: [], playing: [] });
  const [controlLoading, setControlLoading] = useState(false);
  const [controlBusy, setControlBusy] = useState('');
  const [pairSaving, setPairSaving] = useState(false);
  const [requestBusy, setRequestBusy] = useState('');
  const [primaryPlayerId, setPrimaryPlayerId] = useState('');
  const refreshRoundRef = useRef(0);
  const [settings, setSettings] = useState({ playerGap: 150, teamGap: 50, playingAfterMinutes: 2, reservationMinutes: 15 });

  const api = useCallback(async (path, options = {}) => {
    const response = await fetch(`${API_BASE}${path}`, { ...options, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}`, ...(options.headers || {}) } });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      const requestError = new Error(result.message || `HTTP ${response.status}`);
      Object.assign(requestError, result, { status: response.status });
      throw requestError;
    }
    return result;
  }, []);

  const loadControlData = useCallback(async (quiet = false) => {
    if (!quiet) setControlLoading(true);
    try {
      const [queueResult, allMatches] = await Promise.all([
        api(`/api/game-match/queue/${Number(groupId)}`),
        api(`/api/game-match/group/${Number(groupId)}`)
      ]);
      setControlData({
        queue: Array.isArray(queueResult?.matches) ? queueResult.matches : [],
        playing: (Array.isArray(allMatches) ? allMatches : []).filter(match => String(match.status).toLowerCase() === 'playing')
      });
    } catch (controlError) {
      setError(controlError.message);
    } finally {
      setControlLoading(false);
    }
  }, [api, groupId]);

  const load = useCallback(async (quiet = false, round = refreshRoundRef.current) => {
    if (!quiet) setLoading(true);
    try {
      const [result, lockedPairs] = await Promise.all([
        api(`/api/match-planner/${Number(groupId)}?round=${Number(round) || 0}${primaryPlayerId ? `&priorityUserId=${Number(primaryPlayerId)}` : ''}`),
        api(`/api/game-match/locked-pairs/${Number(groupId)}`).catch(() => [])
      ]);
      const existingKeys = new Set((result.waitingPairs || []).map(pair => pairKey(pair.user_a, pair.user_b)));
      const automaticLockedPairs = (Array.isArray(lockedPairs) ? lockedPairs : []).filter(pair => !existingKeys.has(pairKey(pair.user_a, pair.user_b))).map(pair => ({
        id: `locked-${pair.user_a}-${pair.user_b}`,
        group_id: Number(groupId), user_a: pair.user_a, user_b: pair.user_b,
        user_a_name: pair.name_a, user_b_name: pair.name_b,
        status: 'LOCKED', score_gap: '—', pair_source: 'LOCKED'
      }));
      setData({ ...result, lockedPairs: Array.isArray(lockedPairs) ? lockedPairs : [], waitingPairs: [...automaticLockedPairs, ...(result.waitingPairs || [])] }); setError('');
      setSettings({ playerGap: result.settings.player_gap, teamGap: result.settings.team_gap, playingAfterMinutes: result.settings.playing_after_minutes, reservationMinutes: result.settings.reservation_minutes });
    } catch (loadError) { setError(loadError.message); }
    finally { setLoading(false); }
  }, [api, groupId, primaryPlayerId]);

  const refreshNextRound = () => {
    // Move by the number of cards currently shown so the next response starts
    // from a completely new recommendation window instead of replacing only
    // one card while keeping the other seven.
    refreshRoundRef.current += Math.max(1, Number(data?.recommendations?.length || 0));
    setVariantIndexes({});
    setEditedMatches({});
    setEditingMatchId('');
    setApprovedMatches({});
    setSelected('');
    setError('');
    setNotice('');
    load(false, refreshRoundRef.current);
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    connectSocket(); socket.emit('join-group', Number(groupId));
    const refresh = event => { if (!event?.groupId || Number(event.groupId) === Number(groupId)) load(true); };
    ['match-planner-updated','queue-updated','player-status-updated','match-started','match-ended','member-updated'].forEach(name => socket.on(name, refresh));
    return () => ['match-planner-updated','queue-updated','player-status-updated','match-started','match-ended','member-updated'].forEach(name => socket.off(name, refresh));
  }, [groupId, load]);
  useEffect(() => {
    if (!controlOpen) return undefined;
    loadControlData();
    const refreshControl = event => {
      if (!event?.groupId || Number(event.groupId) === Number(groupId)) loadControlData(true);
    };
    ['queue-updated','player-status-updated','match-started','match-ended','member-updated'].forEach(name => socket.on(name, refreshControl));
    return () => ['queue-updated','player-status-updated','match-started','match-ended','member-updated'].forEach(name => socket.off(name, refreshControl));
  }, [controlOpen, groupId, loadControlData]);

  const startControlMatch = async match => {
    setControlBusy(`start-${match.matchId}`);
    try {
      await api('/api/game-match/start', { method: 'POST', body: JSON.stringify({ matchId: match.matchId, groupId: Number(groupId), approvePlaying: false }) });
      setNotice(`✅ เริ่ม Match #${match.groupMatchNo || match.matchId} แล้ว`);
      await Promise.all([loadControlData(true), load(true)]);
    } catch (startError) {
      const message = String(startError.message || 'เริ่ม Match ไม่สำเร็จ');
      if (/approve|active|play|สถานะ/i.test(message) && window.confirm(`${message}\n\nต้องการ Approve และเริ่ม Match หรือไม่?`)) {
        try {
          await api('/api/game-match/start', { method: 'POST', body: JSON.stringify({ matchId: match.matchId, groupId: Number(groupId), approvePlaying: true }) });
          setNotice(`✅ Approve และเริ่ม Match #${match.groupMatchNo || match.matchId} แล้ว`);
          await Promise.all([loadControlData(true), load(true)]);
        } catch (approvedError) { setError(approvedError.message); }
      } else setError(message);
    } finally { setControlBusy(''); }
  };

  const endControlMatch = async (match, winnerTeam) => {
    const label = winnerTeam === 'TEAM1' ? 'T1 ชนะ' : winnerTeam === 'TEAM2' ? 'T2 ชนะ' : 'เสมอ';
    if (!window.confirm(`ยืนยันจบ Match #${match.groupMatchNo || match.matchId} เป็น ${label}?`)) return;
    setControlBusy(`end-${match.matchId}`);
    try {
      await api('/api/game-match/end', { method: 'POST', body: JSON.stringify({ matchId: match.matchId, winnerTeam }) });
      setNotice(`✅ จบ Match #${match.groupMatchNo || match.matchId}: ${label}`);
      await Promise.all([loadControlData(true), load(true)]);
    } catch (endError) { setError(endError.message); }
    finally { setControlBusy(''); }
  };

  const compactPlayers = match => {
    if (Array.isArray(match.players)) return [1,2].map(team => match.players.filter(player => Number(player.team) === team));
    return [match.team1 || [], match.team2 || []];
  };

  const waitingPlayers = [...(data?.players || [])].sort((a, b) =>
    Number(b.wait_minutes || 0) - Number(a.wait_minutes || 0) ||
    Number(a.total_games || 0) - Number(b.total_games || 0) ||
    String(a.name || '').localeCompare(String(b.name || ''), 'th')
  );
  const eligiblePlayerIds = new Set((data?.eligible || []).map(player => Number(player.user_id)));
  const choosePrimaryPlayer = player => {
    if (!eligiblePlayerIds.has(Number(player.user_id))) {
      setError(`${player.name} ยังเลือกเป็นผู้เล่นหลักไม่ได้ เพราะติด Queue หรือสถานะยังไม่พร้อม`);
      return;
    }
    setPrimaryPlayerId(String(player.user_id));
    setWaitingPlayersOpen(false);
    setVariantIndexes({}); setEditedMatches({}); setApprovedMatches({}); setError('');
  };

  const saveSettings = async () => {
    setSaving(true);
    try { await api(`/api/match-planner/${groupId}/settings`, { method: 'PUT', body: JSON.stringify(settings) }); await load(true); }
    catch (saveError) { setError(saveError.message); }
    finally { setSaving(false); }
  };

  const decideRequest = async (request, decision) => {
    const action = decision === 'APPROVE' ? 'Approve' : 'ปฏิเสธ';
    if (!window.confirm(`ยืนยัน ${action} คำขอของ ${request.requester_name}?`)) return;
    setRequestBusy(`${request.id}-${decision}`); setError('');
    try {
      await api(`/api/match-planner/${groupId}/requests/${request.id}/decision`, { method: 'POST', body: JSON.stringify({ decision }) });
      setNotice(decision === 'APPROVE' ? `✅ Approve คำขอ ${request.request_mode} คนแล้ว` : 'ยกเลิกคำขอแล้ว');
      await load(true);
    } catch (requestError) { setError(requestError.message); }
    finally { setRequestBusy(''); }
  };

  const createQueue = async suggestion => {
    const wasEdited = Boolean(editedMatches[suggestion.id]);
    const repeatApproved = Boolean(approvedMatches[suggestion.id]);
    if (suggestion.requiresApproval && !repeatApproved) {
      setError('Match นี้มีผู้เล่นคู่กันหรือตรงข้ามกันครบ 2 รอบแล้ว กรุณากด Approve ก่อนสร้าง Queue');
      return;
    }
    const selectedPlayers = [...suggestion.team1, ...suggestion.team2];
    const queuePlayers = selectedPlayers.filter(player => player.status === 'QUEUE' || player.queued_match_id);
    const repeatWarning = suggestion.requiresApproval
      ? `\n\n✅ Admin Approve ประวัติซ้ำแล้ว:\n- ${(suggestion.repeatViolations || []).join('\n- ')}`
      : '';
    const queueWarning = queuePlayers.length
      ? `\n\n⚠️ เลือกผ่าน Edit และอยู่ใน Queue อื่น: ${queuePlayers.map(player => player.name).join(', ')}\nกดตกลงเพื่อ Approve ให้ผู้เล่นอยู่มากกว่า 1 Queue`
      : '';
    const warning = `${suggestion.relaxed ? `ชุดนี้ผ่อนปรนกติกา: คะแนนผู้เล่นห่าง ${suggestion.playerGap} และคะแนนรวมทีมห่าง ${suggestion.teamGap}` : 'Match นี้ผ่านเกณฑ์ปกติ'}${repeatWarning}${queueWarning}\n\n${suggestion.requiresApproval || queuePlayers.length ? 'กดตกลงเพื่อ Approve และสร้าง Queue' : 'ยืนยันสร้าง Queue?'}`;
    if (!window.confirm(warning)) return;
    setSelected(suggestion.id);
    try {
      const payload = { team1: suggestion.team1.map(p => p.user_id), team2: suggestion.team2.map(p => p.user_id), approveRepeat: repeatApproved, approveQueueConflict: queuePlayers.length > 0 };
      let result;
      try {
        result = await api(`/api/match-planner/${groupId}/queue`, { method: 'POST', body: JSON.stringify(payload) });
      } catch (queueError) {
        if (!queueError.requiresQueueApproval || !window.confirm(`${queueError.message}\n\nยืนยัน Approve และสร้าง Queue หรือไม่?`)) throw queueError;
        result = await api(`/api/match-planner/${groupId}/queue`, { method: 'POST', body: JSON.stringify({ ...payload, approveQueueConflict: true }) });
      }
      setNotice(`✅ สร้าง Match Queue #${result.groupMatchNo} สำเร็จ`);
      if (wasEdited) {
        setEditedMatches(previous => {
          const next = { ...previous };
          delete next[suggestion.id];
          return next;
        });
        setEditingMatchId('');
        setApprovedMatches(previous => {
          const next = { ...previous };
          delete next[suggestion.id];
          return next;
        });
        setVariantIndexes(previous => {
          const next = { ...previous };
          delete next[suggestion.id];
          return next;
        });
        refreshRoundRef.current += 1;
        await load(true, refreshRoundRef.current);
      } else {
        await load(true);
      }
    } catch (queueError) { setError(queueError.message); }
    finally { setSelected(''); }
  };

  // Waiting Pair is an explicit Admin action, so its search must not be limited
  // by the smaller calculation window used for automatic recommendations.
  const selectable = (data?.players || []).filter(player => player.status !== 'HOLD');
  const [pairA, setPairA] = useState(null);
  const [pairB, setPairB] = useState(null);
  const createPair = async (suggestedA = pairA, suggestedB = pairB) => {
    const userA = typeof suggestedA === 'object' ? suggestedA?.user_id : suggestedA;
    const userB = typeof suggestedB === 'object' ? suggestedB?.user_id : suggestedB;
    const occupied = (data?.waitingPairs || []).find(pair => [Number(pair.user_a),Number(pair.user_b)].some(id => id===Number(userA)||id===Number(userB)));
    if (occupied) { setError(`สร้างคู่ไม่ได้: ${occupied.user_a_name} + ${occupied.user_b_name} ถูกล็อกเป็นคู่อยู่แล้ว`); return; }
    if (pairSaving) return;
    setPairSaving(true);
    try { await api(`/api/match-planner/${groupId}/pairs`, { method: 'POST', body: JSON.stringify({ userA, userB }) }); setPairA(null); setPairB(null); await load(true); }
    catch (pairError) { setError(pairError.message); }
    finally { setPairSaving(false); }
  };

  const protectedPairFor = player => (data?.waitingPairs || []).find(pair =>
    [Number(pair.user_a), Number(pair.user_b)].includes(Number(player?.user_id))
  );
  const editablePlayers = (data?.players || []).filter(player => player.status !== 'HOLD' && !protectedPairFor(player));
  const recheckMatchHistory = (matchId, match) => {
    const checkingMatch = { ...match, historyChecking: true };
    const team1Ids = checkingMatch.team1.map(player => Number(player.user_id));
    const team2Ids = checkingMatch.team2.map(player => Number(player.user_id));
    const selectionKey = [...team1Ids, ...team2Ids].join('-');
    setEditedMatches(previous => ({ ...previous, [matchId]: checkingMatch }));
    setApprovedMatches(previous => { const next = { ...previous }; delete next[matchId]; return next; });
    api('/api/match-planner/' + groupId + '/check-history', {
      method: 'POST', body: JSON.stringify({ team1: team1Ids, team2: team2Ids })
    }).then(historyResult => setEditedMatches(previous => {
      const latest = previous[matchId];
      if (!latest || [...latest.team1, ...latest.team2].map(player => Number(player.user_id)).join('-') !== selectionKey) return previous;
      return { ...previous, [matchId]: { ...latest, ...historyResult, historyChecking: false } };
    })).catch(historyError => {
      setEditedMatches(previous => {
        const latest = previous[matchId];
        if (!latest || [...latest.team1, ...latest.team2].map(player => Number(player.user_id)).join('-') !== selectionKey) return previous;
        return { ...previous, [matchId]: { ...latest, historyChecking: false } };
      });
      setError('ตรวจประวัติคู่ใหม่ไม่สำเร็จ: ' + historyError.message);
    });
  };
  const updateMatchPlayer = (matchId, current, teamKey, playerIndex, replacement) => {
    const next = editedMatches[matchId]
      ? { ...editedMatches[matchId], team1: [...editedMatches[matchId].team1], team2: [...editedMatches[matchId].team2] }
      : { ...current, team1: [...current.team1], team2: [...current.team2] };
    next[teamKey][playerIndex] = replacement;
    next.sum1 = next.team1.reduce((sum, player) => sum + Number(player.point || 0), 0);
    next.sum2 = next.team2.reduce((sum, player) => sum + Number(player.point || 0), 0);
    next.teamGap = Math.abs(next.sum1 - next.sum2);
    const points = [...next.team1, ...next.team2].map(player => Number(player.point || 0));
    next.playerGap = Math.max(...points) - Math.min(...points);
    next.relaxed = next.playerGap > Number(settings.playerGap) || next.teamGap > Number(settings.teamGap);
    next.historyChecking = true;
    setEditedMatches(previous => ({ ...previous, [matchId]: next }));
    setApprovedMatches(previous => {
      const nextApprovals = { ...previous };
      delete nextApprovals[matchId];
      return nextApprovals;
    });
    const team1Ids = next.team1.map(player => Number(player.user_id));
    const team2Ids = next.team2.map(player => Number(player.user_id));
    const selectionKey = [...team1Ids, ...team2Ids].join('-');
    api(`/api/match-planner/${groupId}/check-history`, {
      method: 'POST',
      body: JSON.stringify({ team1: team1Ids, team2: team2Ids })
    }).then(historyResult => {
      setEditedMatches(previous => {
        const latest = previous[matchId];
        if (!latest || [...latest.team1, ...latest.team2].map(player => Number(player.user_id)).join('-') !== selectionKey) return previous;
        return { ...previous, [matchId]: { ...latest, ...historyResult, historyChecking: false } };
      });
    }).catch(historyError => {
      setEditedMatches(previous => {
        const latest = previous[matchId];
        if (!latest || [...latest.team1, ...latest.team2].map(player => Number(player.user_id)).join('-') !== selectionKey) return previous;
        return { ...previous, [matchId]: { ...latest, historyChecking: false } };
      });
      setError(`ตรวจประวัติคู่ใหม่ไม่สำเร็จ: ${historyError.message}`);
    });
  };

  const renderPlayer = player => <div className={`mp-player ${player.status === 'PLAY' ? 'is-play' : ''}`} key={player.user_id}>
    <div className="mp-player-main"><div><strong>{player.name}</strong><span>{player.level_rank} · {player.point} คะแนน</span></div><small>{player.status}{player.status === 'PLAY' ? ` ${player.playing_minutes} นาที` : ` · รอ ${player.wait_minutes || 0} นาที`}</small></div>
  </div>;

  return <div className="mp-page admin-shell-with-sidebar">
    <AdminSidebar active="planner" groupId={groupId} onRefresh={refreshNextRound} />
    <header className="mp-header"><div><button onClick={() => navigate('/dashboard')}>← Dashboard</button><p>MATCH PLANNER</p><h1>จัดแมตช์ให้ทัน และสูสีที่สุด</h1><span>จับคู่ 2 คนก่อน · จองผู้เล่นที่ใกล้จบ · ส่งเข้า Queue เมื่อพร้อม</span></div><div className="mp-header-actions"><button className="mp-control-open" onClick={()=>setControlOpen(true)}>🏸 Queue / Match Play</button><button className="mp-refresh" onClick={refreshNextRound}>↻ คิวแนะนำถัดไป</button></div></header>
    {error && <div className="mp-error">⚠️ {error}</div>}
    {notice && <div className="mp-notice">{notice}<button onClick={()=>setNotice('')}>×</button></div>}
    <section className="mp-settings"><div><h2>⚙️ เกณฑ์การจัด</h2><p>ขยายค่าได้เมื่อต้องการเน้นให้ผู้เล่นได้ลงสนาม</p></div>
      <label>คะแนนผู้เล่นห่างไม่เกิน<input type="number" value={settings.playerGap} onChange={e => setSettings(s => ({...s, playerGap:e.target.value}))}/></label>
      <label>คะแนนรวมทีมห่างไม่เกิน<input type="number" value={settings.teamGap} onChange={e => setSettings(s => ({...s, teamGap:e.target.value}))}/></label>
      <label>จองคน PLAY เมื่อเล่นอย่างน้อย<input type="number" min="2" value={settings.playingAfterMinutes} onChange={e => setSettings(s => ({...s, playingAfterMinutes:e.target.value}))}/></label>
      <label>หมดเวลาจองภายใน<input type="number" value={settings.reservationMinutes} onChange={e => setSettings(s => ({...s, reservationMinutes:e.target.value}))}/></label>
      <button onClick={saveSettings} disabled={saving}>{saving ? 'กำลังบันทึก…' : 'บันทึกและคำนวณใหม่'}</button>
    </section>
    <div className="mp-summary"><button className="mp-waiting-open" onClick={()=>setWaitingPlayersOpen(true)}>⏱ ผู้เล่นรอนาน <b>{waitingPlayers.length}</b> คน</button><span>พร้อมพิจารณา <b>{data?.eligible?.length || 0}</b> คน</span><span>ACTIVE <b>{data?.players?.filter(p=>p.status==='ACTIVE').length || 0}</b></span><span>PLAY ที่จองได้ <b>{data?.eligible?.filter(p=>p.status==='PLAY').length || 0}</b></span><span>คำแนะนำ <b>{data?.recommendations?.length || 0}</b> ชุด</span></div>
    <main className="mp-grid">
      <section className="mp-column mp-wide"><div className="mp-title"><div><h2>✨ Match แนะนำ</h2><p>ตัดคู่ทีม/คู่ตรงข้ามซ้ำ 2 รอบแล้วเปลี่ยนคน · ACTIVE ไม่พอจึงดึง PLAY · Manual Edit สามารถ Approve ได้</p></div></div>
        <div className="mp-primary-player"><div><b>🎯 ผู้เล่นหลักที่กำลังนำมาสร้าง Match</b><span>ทุกคำแนะนำจะพยายามจัดให้มีผู้เล่นคนนี้</span></div><div className="mp-primary-search"><PlayerSearchDropdown value={data?.priorityPlayer || null} allPlayers={data?.eligible || []} usedIds={[]} onChange={choosePrimaryPlayer}/>{primaryPlayerId&&<button type="button" onClick={()=>setPrimaryPlayerId('')}>ใช้คนรอนานอัตโนมัติ</button>}</div></div>
        {loading ? <div className="mp-empty">กำลังวิเคราะห์…</div> : !data?.recommendations?.length ? <div className="mp-empty"><b>ยังประกอบ Match 4 คนไม่ได้</b><span>ระบบจะตรวจใหม่แบบเรียลไทม์ หรือเพิ่มระยะคะแนนเพื่อเปิดโอกาสให้ลงสนาม</span></div> : <div className="mp-recommendations">{data.recommendations.map((item,index) => {
          const alternatives = localAlternatives(item, data?.waitingPairs || []);
          const variantIndex = Math.min(variantIndexes[item.id] || 0, alternatives.length - 1);
          const calculated = { ...item, ...alternatives[variantIndex] };
          const current = editedMatches[item.id] || calculated;
          const usedIds = [...current.team1, ...current.team2].map(player => player.user_id);
          return <article className={`mp-match ${current.relaxed?'is-warning':''}`} key={item.id}>
            <div className="mp-match-head"><span>ตัวเลือก {index+1}</span><div><button className="mp-edit" onClick={()=>setEditingMatchId(editingMatchId===item.id?'':item.id)}>{editingMatchId===item.id?'✓ เสร็จ':'✏️ Edit ผู้เล่น'}</button><b className={current.status==='READY'?'ready':'reserved'}>{statusLabel(current.status)}</b></div></div>
            <div className="mp-teams"><div><h3>Team 1 · {current.sum1}</h3>{current.team1.map((player,playerIndex)=>editingMatchId===item.id?<div className="mp-edit-player" key={player.user_id}>{protectedPairFor(player)?<div className="mp-fixed-player">{renderPlayer(player)}<small>🔒 แก้แยกไม่ได้</small></div>:<PlayerSearchDropdown value={player} allPlayers={editablePlayers} usedIds={usedIds.filter(id=>Number(id)!==Number(player.user_id))} onChange={replacement=>updateMatchPlayer(item.id,current,'team1',playerIndex,replacement)}/>}</div>:renderPlayer(player))}</div><strong>VS</strong><div><h3>Team 2 · {current.sum2}</h3>{current.team2.map((player,playerIndex)=>editingMatchId===item.id?<div className="mp-edit-player" key={player.user_id}>{protectedPairFor(player)?<div className="mp-fixed-player">{renderPlayer(player)}<small>🔒 แก้แยกไม่ได้</small></div>:<PlayerSearchDropdown value={player} allPlayers={editablePlayers} usedIds={usedIds.filter(id=>Number(id)!==Number(player.user_id))} onChange={replacement=>updateMatchPlayer(item.id,current,'team2',playerIndex,replacement)}/>}</div>:renderPlayer(player))}</div></div>
            <div className="mp-metrics"><span>ผู้เล่นห่าง <b>{current.playerGap}</b></span><span>ทีมต่าง <b>{current.teamGap}</b></span><span>Rank ห่าง <b>{current.rankGap}</b></span><span>คู่ซ้ำ <b>{current.repeatPartners}</b></span></div>
            <div className="mp-pair-details">
              <b>คู่ที่จัดมา</b>
              {current.historyChecking && <span className="is-checking">กำลังตรวจประวัติชุดใหม่…</span>}
              {(current.teammateDetails || []).map((pair,pairIndex)=><span className={pair.count>=2&&!pair.locked?'is-repeat':''} key={`team-${pairIndex}`}>{pair.locked?'🔒 ':'👥 '}{pair.names} · เคยคู่ {pair.count} รอบ</span>)}
            </div>
            {current.requiresApproval && <div className="mp-repeat-alert"><b>⚠️ ผู้เล่นครบ 2 รอบแล้ว</b>{(current.repeatViolations || []).map((message,violationIndex)=><span key={`violation-${violationIndex}`}>{message}</span>)}</div>}
            {current.relaxed && <p className="mp-warning">⚠️ ผ่อนปรนคะแนน/Rank เพื่อให้จัด Match ได้ โดย Waiting Pair และ Locked Pair ยังอยู่ทีมเดียวกันตลอด</p>}
            <div className="mp-match-actions">
              <button className="mp-swap" disabled={editingMatchId===item.id||current.historyChecking} onClick={() => { const nextIndex=(variantIndex+1)%alternatives.length;setVariantIndexes(previous=>({...previous,[item.id]:nextIndex}));recheckMatchHistory(item.id,{...item,...alternatives[nextIndex]}); }}>⇄ สลับคู่ {alternatives.length > 1 ? `(${variantIndex + 1}/${alternatives.length})` : ''}</button>
              {current.requiresApproval && <button className={`mp-approve ${approvedMatches[item.id]?'is-approved':''}`} disabled={Boolean(approvedMatches[item.id])} onClick={()=>{setApprovedMatches(previous=>({...previous,[item.id]:true}));setError('');}}>{approvedMatches[item.id]?'✓ Approve แล้ว':'🔓 Approve'}</button>}
              <button className="mp-queue" disabled={selected===item.id||current.historyChecking||(current.requiresApproval&&!approvedMatches[item.id])} onClick={()=>createQueue(current)}>{selected===item.id?'กำลังสร้าง…':current.historyChecking?'กำลังตรวจประวัติ…':current.requiresApproval&&!approvedMatches[item.id]?'รอ Approve':current.status==='READY'?'ส่งเข้า Match Queue':'จองเป็น Queue ล่วงหน้า'}</button>
            </div>
          </article>;
        })}</div>}
      </section>
      <aside className="mp-side">
        <section className="mp-card"><h2>💡 คู่ 2 คนแนะนำ</h2><p>เรียงจากคู่ล็อก เวลารอ และคะแนนใกล้กัน</p>{!data?.recommendedPairs?.length?<p className="mp-muted">ยังไม่มีคู่ที่เหมาะสม</p>:data.recommendedPairs.map(pair=><div className={`mp-suggested-pair ${pair.isLocked?'is-locked':''}`} key={pair.id}><div><b>{pair.players[0].name} + {pair.players[1].name}</b><span>{pair.players[0].level_rank}/{pair.players[1].level_rank} · ห่าง {pair.pointGap} คะแนน · {pair.status}</span>{pair.isLocked&&<em>🔒 คู่ Locked ต้องอยู่ด้วยกัน</em>}{pair.hasReservedMember&&<em>มีผู้เล่นติดคู่อื่นแล้ว</em>}</div><button disabled={pair.alreadyPaired||pair.hasReservedMember||pairSaving} onClick={()=>createPair(pair.players[0].user_id,pair.players[1].user_id)}>{pair.alreadyPaired?'ล็อกแล้ว':pair.hasReservedMember?'ติดคู่อื่น':'ล็อกคู่นี้'}</button></div>)}</section>
        <section className="mp-card"><h2>👥 สร้าง Waiting Pair</h2><p>ค้นหาผู้เล่นทั้งหมดด้วยชื่อ Rank คะแนน หรือสถานะ</p><div className="mp-pair-search"><PlayerSearchDropdown value={pairA} allPlayers={selectable} usedIds={[pairB?.user_id].filter(Boolean)} onChange={setPairA}/><PlayerSearchDropdown value={pairB} allPlayers={selectable} usedIds={[pairA?.user_id].filter(Boolean)} onChange={setPairB}/></div><button disabled={!pairA||!pairB||pairSaving} onClick={()=>createPair()}>{pairSaving?'กำลังล็อก…':'+ ล็อก Waiting Pair'}</button></section>
        <section className="mp-card"><h2>🔒 Waiting Pairs</h2><p>รวม Waiting Pair และ Locked Pair จากหน้าจัด Match</p>{!data?.waitingPairs?.length?<p className="mp-muted">ยังไม่มีคู่ที่ล็อกไว้</p>:data.waitingPairs.map(pair=><div className={`mp-pair ${pair.pair_source==='LOCKED'?'is-locked':''}`} key={pair.id}><div><b>{pair.user_a_name} + {pair.user_b_name}</b><span>{pair.status} · ห่าง {pair.score_gap} คะแนน</span>{pair.pair_source==='LOCKED'&&<em>🔒 LOCKED PAIR · อยู่ด้วยกันตลอด</em>}</div>{pair.pair_source==='LOCKED'?<span className="mp-lock-badge">ล็อกถาวร</span>:<button title="ยกเลิก Waiting Pair" onClick={async()=>{await api(`/api/match-planner/${groupId}/pairs/${pair.id}`,{method:'DELETE'});load(true);}}>×</button>}</div>)}</section>
        <section className="mp-card"><h2>📨 คำขอจากผู้เล่น</h2><p>ตรวจรายชื่อก่อน Approve เพื่อส่งคู่เข้า Waiting Pair หรือ Match เข้า Queue</p>{!data?.requests?.length?<p className="mp-muted">ยังไม่มีคำขอที่เปิดอยู่</p>:data.requests.map(request=><div className="mp-player-request" key={request.id}><header><b>{request.requester_name}</b><span>{request.request_mode} คน</span></header><div>{(request.players||[]).map((player,index)=><span key={player.user_id}>{index+1}. {player.name} · {player.level_rank} · {player.status}</span>)}</div>{request.note&&<p>หมายเหตุ: {request.note}</p>}<footer><button disabled={Boolean(requestBusy)} onClick={()=>decideRequest(request,'REJECT')}>ยกเลิก</button><button disabled={Boolean(requestBusy)} onClick={()=>decideRequest(request,'APPROVE')}>{requestBusy===`${request.id}-APPROVE`?'กำลัง Approve…':'✓ Approve'}</button></footer></div>)}</section>
      </aside>
    </main>
    {controlOpen && <div className="mp-control-backdrop" role="presentation" onMouseDown={event=>{if(event.target===event.currentTarget)setControlOpen(false);}}>
      <section className="mp-control-modal" role="dialog" aria-modal="true" aria-label="Queue และ Match Play">
        <header><div><h2>🏸 Queue & Match Play</h2><p>เริ่มสนามและจบผลการแข่งขันได้จากหน้านี้</p></div><button onClick={()=>setControlOpen(false)}>×</button></header>
        <div className="mp-control-tabs-summary"><span>Queue <b>{controlData.queue.length}</b></span><span>กำลังเล่น <b>{controlData.playing.length}</b></span><button onClick={()=>loadControlData()}>↻ Refresh</button></div>
        {controlLoading ? <div className="mp-control-empty">กำลังโหลด…</div> : <div className="mp-control-columns">
          <div><h3>⏳ Match Queue</h3>{!controlData.queue.length?<div className="mp-control-empty">ไม่มี Match ใน Queue</div>:controlData.queue.map(match=>{const [team1,team2]=compactPlayers(match);return <article className="mp-control-match" key={`q-${match.matchId}`}><div className="mp-control-match-head"><b>Match #{match.groupMatchNo || match.matchId}</b><span>รอเริ่ม</span></div><div className="mp-control-teams"><span><i>T1</i>{team1.map(player=>player.name).join(' • ')}</span><em>VS</em><span><i>T2</i>{team2.map(player=>player.name).join(' • ')}</span></div><button className="mp-control-start" disabled={Boolean(controlBusy)} onClick={()=>startControlMatch(match)}>{controlBusy===`start-${match.matchId}`?'กำลังเริ่ม…':'▶ Start Match'}</button></article>})}</div>
          <div><h3>⚡ Match Play</h3>{!controlData.playing.length?<div className="mp-control-empty">ยังไม่มี Match กำลังเล่น</div>:controlData.playing.map(match=>{const [team1,team2]=compactPlayers(match);return <article className="mp-control-match is-playing" key={`p-${match.matchId}`}><div className="mp-control-match-head"><b>Match #{match.groupMatchNo || match.matchId}</b><span>Court {match.court_no || '-'}</span></div><div className="mp-control-teams"><span><i>T1</i>{team1.map(player=>player.name).join(' • ')}</span><em>VS</em><span><i>T2</i>{team2.map(player=>player.name).join(' • ')}</span></div><div className="mp-control-results"><button disabled={Boolean(controlBusy)} onClick={()=>endControlMatch(match,'TEAM1')}>T1 Win</button><button disabled={Boolean(controlBusy)} onClick={()=>endControlMatch(match,'DRAW')}>Draw</button><button disabled={Boolean(controlBusy)} onClick={()=>endControlMatch(match,'TEAM2')}>T2 Win</button></div></article>})}</div>
        </div>}
      </section>
    </div>}
    {waitingPlayersOpen && <div className="mp-control-backdrop" role="presentation" onMouseDown={event=>{if(event.target===event.currentTarget)setWaitingPlayersOpen(false);}}>
      <section className="mp-waiting-modal" role="dialog" aria-modal="true" aria-label="ผู้เล่นรอนาน">
        <header><div><h2>⏱ ผู้เล่นรอนาน</h2><p>ยังไม่ถูกจัดขึ้นก่อน ตามด้วย Queue และ Play · ภายในสถานะเรียงเวลารอนานที่สุด</p></div><button onClick={()=>setWaitingPlayersOpen(false)}>×</button></header>
        <div className="mp-waiting-list">
          {!waitingPlayers.length?<div className="mp-control-empty">ยังไม่มีผู้เล่นที่กำลังรอ</div>:waitingPlayers.map((player,index)=><article key={player.user_id} className={`is-${String(player.status||'').toLowerCase()} ${Number(data?.priorityPlayer?.user_id)===Number(player.user_id)?'is-primary':''}`}>
            <strong>{index+1}</strong><div><b>{player.name}</b><span>{player.level_rank} · {Number(player.point||0)} คะแนน · เล่นแล้ว {Number(player.total_games||0)} เกม</span></div><aside><b>{player.status==='PLAY' ? `เล่น ${Number(player.playing_minutes||0)} นาที` : `รอ ${Number(player.wait_minutes||0)} นาที`}</b><small>{player.status==='ACTIVE'?'ยังไม่ถูกจัด':player.status}</small><button type="button" disabled={!eligiblePlayerIds.has(Number(player.user_id))} onClick={()=>choosePrimaryPlayer(player)}>{Number(data?.priorityPlayer?.user_id)===Number(player.user_id)?'กำลังจัด':'เลือกจัด'}</button></aside>
          </article>)}
        </div>
      </section>
    </div>}
  </div>;
}

export default MatchPlanner;
